<template>
  <nav aria-label="パンくずリスト">
    <div class="row container-fluid mx-0 mb-md-0 mb-5 px-0 py-4 size-12 d-none d-md-block">
      <div class="px-0 text-nowrap">
        <ol class="m-0 p-0" itemscope itemtype="https://schema.org/BreadcrumbList">
          <li
            v-for="(item, index) in currentBreadcrumbs"
            :key="index"
            class="d-inline-block"
            itemprop="itemListElement"
            itemscope
            itemtype="https://schema.org/ListItem"
          >
            <a
              v-if="item.href"
              :href="item.href"
              class="color-blue-primary text-decoration-none"
              itemprop="item"
            >
              <span itemprop="name">{{ item.text }}</span>
            </a>
            <span v-else itemprop="name">{{ item.text }}</span>
            <meta itemprop="position" :content="String(index + 1)" />
            <span v-if="index < currentBreadcrumbs.length - 1" class="mx-1">></span>
          </li>
        </ol>
      </div>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from 'vue';
import { LocationQueryValue, useRoute } from 'vue-router';

interface CategoryItem {
  id: string;
  name: string;
  ag_category_id: string;
  children: Array<CategoryItem>;
}

interface BreadcrumbItem {
  text: string;
  href: string;
}

interface Props {
  isDirect?: boolean;
  prefectures: CategoryItem[];
  occupations: CategoryItem[];
  industries: CategoryItem[];
  skills: CategoryItem[];
  jobFeatures: CategoryItem[];
}

const props = withDefaults(defineProps<Props>(), {
  isDirect: false,
});
const route = useRoute();
const currentBreadcrumbs = ref<BreadcrumbItem[]>([]);

/**
 * クエリパラメータの値を取得する
 * @param param - クエリパラメータの値（単一または配列）
 * @returns 文字列としてのパラメータ値
 */
const getQueryParamValue = (param: LocationQueryValue | LocationQueryValue[]): string => {
  if (!param) return '';
  if (Array.isArray(param)) {
    return param[0] || '';
  }
  return param;
};

/**
 * パンくずリストの項目を生成する
 * @param paramKey - クエリパラメータのキー
 * @param paramValue - クエリパラメータの値
 * @returns パンくずリストの項目の配列
 */
const getBreadcrumbItem = (paramKey: string, paramValue: string): BreadcrumbItem[] => {
  const items: BreadcrumbItem[] = [];

  switch (paramKey) {
    case 'prefectures': {
      const childItem = props.prefectures
        .flatMap((item) => item.children)
        .find((child) => Number(child.id) === Number(paramValue));
      if (childItem?.name) {
        items.push({ text: childItem.name, href: '' });
      }
      break;
    }
    case 'job_types':
    case 'industries':
    case 'skills':
    case 'job_features': {
      const categoryData = {
        job_types: props.occupations,
        industries: props.industries,
        skills: props.skills,
        job_features: props.jobFeatures,
      }[paramKey];

      const childItem = categoryData
        .flatMap((item) => item.children)
        .find((child) => Number(child.id) === Number(paramValue));
      if (childItem) {
        const parentItem = categoryData.find((item) =>
          item.children.find((child) => child.id === childItem.id)
        );
        if (parentItem) {
          // 親カテゴリをリンク付きで追加
          const baseUrl = '/jobs';
          const queryParams = new URLSearchParams();
          // agent, directのどちらかをセット
          if (route.query.direct === 'true') {
            queryParams.set('direct', 'true');
          } else if (route.query.agent === 'true') {
            queryParams.set('agent', 'true');
          }
          parentItem.children.forEach((child) => {
            queryParams.append(paramKey, child.id);
          });
          items.push({ text: parentItem.name, href: `${baseUrl}?${queryParams.toString()}` });

          // 子項目をテキストのみで追加
          items.push({ text: childItem.name, href: '' });
        }
      }
      break;
    }
    case 'keywords': {
      items.push({ text: `キーワード: ${paramValue}`, href: '' });
      break;
    }
  }

  return items;
};

/**
 * 複数のカテゴリが選択された場合のパンくずリスト項目を生成する
 * @param paramKey - クエリパラメータのキー
 * @param paramValue - クエリパラメータの値
 * @param categoryData - カテゴリデータの配列
 * @param items - パンくずリストの項目を追加する配列
 */
const handleMultipleCategories = (
  paramKey: string,
  paramValue: LocationQueryValue | LocationQueryValue[],
  categoryData: CategoryItem[],
  items: BreadcrumbItem[]
): void => {
  const ids = Array.isArray(paramValue) ? paramValue : paramValue ? [paramValue] : [];
  const parentCategories = new Set<string>();

  for (const id of ids) {
    if (!id) continue;
    const childItem = categoryData
      .flatMap((item) => item.children)
      .find((child) => Number(child.id) === Number(id));
    if (childItem) {
      const parentItem = categoryData.find((item) =>
        item.children.find((child) => child.id === childItem.id)
      );
      if (parentItem) {
        parentCategories.add(parentItem.name);
        if (parentCategories.size > 1) return;
      }
    }
  }
  parentCategories.forEach((categoryName) => {
    items.push({ text: categoryName, href: '' });
  });
};

/**
 * パンくずリストを更新する
 * 現在のルートのクエリパラメータに基づいてパンくずリストを生成する
 */
const updateBreadcrumbs = (): void => {
  const items: BreadcrumbItem[] = [
    { text: '社内SE転職ナビ', href: '/' },
    { text: '求人検索', href: '/jobs?agent=true' },
  ];

  // ダイレクト求人の場合
  if (props.isDirect) {
    items.push({ text: 'ダイレクト求人', href: '/jobs?direct=true' });
  }

  // クエリパラメータの数を取得（agentとdirectを除外）
  const queryParamsCount = Object.keys(route.query).filter((key) => {
    if (key === 'direct' || key === 'agent') return false;

    const value = route.query[key];
    if (!value) return false;

    // 配列の場合は1つのみの値を許可
    if (Array.isArray(value)) {
      return value.length === 1 && value[0] !== undefined && value[0] !== '';
    }

    return value !== undefined && value !== '';
  }).length;

  // 各カテゴリの値を取得
  const jobTypes = route.query.job_types;
  const industries = route.query.industries;
  const skills = route.query.skills;
  const jobFeatures = route.query.job_features;
  const prefectures = route.query.prefectures;
  const keywords = route.query.keywords;

  // 選択されているカテゴリの数をカウント
  const selectedCategories = [
    jobTypes,
    industries,
    skills,
    jobFeatures,
    prefectures,
    keywords,
  ].filter((param) => {
    if (!param) return false;
    if (Array.isArray(param)) return param.length > 0;
    return param !== '';
  });

  const hasMultipleJobTypes =
    jobTypes &&
    ((Array.isArray(jobTypes) && jobTypes.length > 1) ||
      (typeof jobTypes === 'string' && jobTypes.includes(',')));

  const hasMultipleIndustries =
    industries &&
    ((Array.isArray(industries) && industries.length > 1) ||
      (typeof industries === 'string' && industries.includes(',')));

  const hasMultipleSkills =
    skills &&
    ((Array.isArray(skills) && skills.length > 1) ||
      (typeof skills === 'string' && skills.includes(',')));

  const hasMultipleJobFeatures =
    jobFeatures &&
    ((Array.isArray(jobFeatures) && jobFeatures.length > 1) ||
      (typeof jobFeatures === 'string' && jobFeatures.includes(',')));

  // 2つ以上のカテゴリが選択されている場合は追加のパンくずを表示しない
  if (selectedCategories.length > 1) {
    currentBreadcrumbs.value = items;
    return;
  }

  if (hasMultipleJobTypes) {
    handleMultipleCategories('job_types', jobTypes, props.occupations, items);
  } else if (hasMultipleIndustries) {
    handleMultipleCategories('industries', industries, props.industries, items);
  } else if (hasMultipleSkills) {
    handleMultipleCategories('skills', skills, props.skills, items);
  } else if (hasMultipleJobFeatures) {
    handleMultipleCategories('job_features', jobFeatures, props.jobFeatures, items);
  } else if (queryParamsCount === 1) {
    // クエリパラメータが1つのみの場合
    const paramKey = Object.keys(route.query).find((key) => {
      if (key === 'direct' || key === 'agent') return false;

      const value = route.query[key];
      if (!value) return false;

      // 配列の場合は1つのみの値を許可
      if (Array.isArray(value)) {
        return value.length === 1 && value[0] !== undefined && value[0] !== '';
      }

      return value !== undefined && value !== '';
    });

    if (paramKey) {
      const paramValue = getQueryParamValue(route.query[paramKey]);
      const breadcrumbItems = getBreadcrumbItem(paramKey, paramValue);
      items.push(...breadcrumbItems);
    }
  }
  currentBreadcrumbs.value = items;
};

// 初期表示時にパンくずリストを更新
onMounted(() => {
  updateBreadcrumbs();
});

// クエリパラメータの変更を監視
watch(
  () => route.query,
  () => {
    updateBreadcrumbs();
  },
  { deep: true }
);

// propsの変更を監視
watch(
  () => [props.prefectures, props.occupations, props.industries, props.skills, props.jobFeatures],
  () => {
    updateBreadcrumbs();
  },
  { deep: true }
);
</script>

<style scoped lang="scss"></style>
